import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { streamTarotReading, logStaticReading, RateLimitError } from "../../Services/aiReading";
import { useSpeechContext } from "../../Services/Context/SpeechContext";
import { useLanguage } from "../../Services/Context/LanguageContext";
import StaticCardMeaning from "../StaticCardMeaning/StaticCardMeaning";
import { cardMeanings } from "../../Services/cardMeanings";
import "./ReadingDisplay.css";

interface ReadingDisplayProps {
  question: string;
  cardName: string;
  isReversed: boolean;
  onDrawAgain: () => void;
  onDrawClarifying?: () => void;
  clarifyingActive?: boolean;
  aiEnabled?: boolean;
  isYesOrNo?: boolean;
  rateLimitError?: string | null;
}

const ReadingDisplay = ({
  question,
  cardName,
  isReversed,
  onDrawAgain,
  onDrawClarifying,
  clarifyingActive,
  aiEnabled = true,
  isYesOrNo = false,
  rateLimitError,
}: ReadingDisplayProps) => {
  const { language, translate } = useLanguage();
  const langRef = useRef(language);
  const [text, setText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const { speak } = useSpeechContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!aiEnabled) {
      setIsComplete(true);
      let active = true;
      const timer = setTimeout(() => {
        if (active) logStaticReading(cardName, isReversed, question);
      }, 0);
      return () => {
        active = false;
        clearTimeout(timer);
      };
    }

    let cancelled = false;
    const controller = new AbortController();
    setText("");
    setIsComplete(false);

    const fetchReading = async () => {
      try {
        const generator = streamTarotReading(
          cardName,
          isReversed,
          question,
          undefined,
          controller.signal,
          undefined,
          langRef.current,
          undefined,
          undefined,
          undefined,
          true,
          isYesOrNo,
        );
        for await (const chunk of generator) {
          if (cancelled) break;
          setText((prev) => prev + chunk);
        }
        if (!cancelled) setIsComplete(true);
      } catch (err) {
        if (cancelled) return;
        console.error("Tarot reading error:", err);
        setText(
          err instanceof RateLimitError
            ? err.message
            : translate("readingError"),
        );
        setIsComplete(true);
      }
    };

    fetchReading();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [cardName, isReversed, question, aiEnabled, isYesOrNo, translate]);

  // Auto-speak when reading is complete
  useEffect(() => {
    if (!isComplete) return;
    if (aiEnabled && text) {
      speak(text);
    } else if (!aiEnabled) {
      const data = cardMeanings[cardName];
      if (data) {
        const meaning = isReversed ? data.reversedMeaning : data.meaning;
        speak(`${cardName}. ${meaning} ${data.symbolism}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  return (
    <motion.div
      className="ReadingDisplay"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
    >
      <div ref={topRef} />
      <div className="ReadingDisplay-header">
        <h2 className="ReadingDisplay-card-name">
          {cardName}
          {isReversed && (
            <span className="ReadingDisplay-reversed">
              {" "}
              · {translate("reversed")}
            </span>
          )}
        </h2>
        <p className="ReadingDisplay-question">"{question}"</p>
      </div>

      <div className="ReadingDisplay-divider" />

      {aiEnabled ? (
        <div className="ReadingDisplay-text">
          {text}
          {!isComplete && (
            <span className="ReadingDisplay-cursor" aria-hidden="true">
              ▋
            </span>
          )}
        </div>
      ) : (
        <StaticCardMeaning cardName={cardName} isReversed={isReversed} />
      )}

      {isComplete && !clarifyingActive && (
        <motion.div
          className="ReadingDisplay-actions"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          {rateLimitError && (
            <p className="ReadingDisplay-rate-limit">{rateLimitError}</p>
          )}
          {onDrawClarifying && (
            <motion.button
              className="ReadingDisplay-button ReadingDisplay-button--clarify"
              onClick={onDrawClarifying}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              {translate("drawClarifying")}
            </motion.button>
          )}
          <motion.button
            className="ReadingDisplay-button"
            onClick={onDrawAgain}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            {translate("drawAgain")}
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ReadingDisplay;
